import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Button from '../Button/Button';
import themeColors from '../../assets/styles/themeColors';
import { mediaBelow, pxToRem } from '../../assets/styles/helper';
import { ReactComponent as Insurace } from '../../assets/images/svg/insurance.svg';
import { ReactComponent as VaultIcon } from '../../assets/images/svg/erasebg-transformed.svg';
import { ReactComponent as FinancialProfit } from '../../assets/images/svg/financial-profit.svg';
import { ReactComponent as Close } from '../../assets/images/svg/close.svg';
import {
  getCurrentAccountCode,
  getCurrentAccountUid,
} from '../../store/selectors/accountSelectors';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import { authScopeStringGetHelper } from '../../util/helpers/authScopeHelpers';
import { JWT_TOKEN } from '../../constants/localStorage';
import { variables } from '../../assets/styles/variables';

const ModalOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.4);
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const UpgradeModal = styled.div`
  position: absolute;
  left: ${pxToRem(16)};
  right: ${pxToRem(16)};
  bottom: ${pxToRem(15)};
  background-color: ${themeColors.colorWhite};
  padding: ${pxToRem(16)};
  border-radius: ${pxToRem(10)};
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const Title = styled.p`
  font-size: ${pxToRem(16)};
  line-height: ${pxToRem(20)};
  font-weight: 600;
  color: ${themeColors.colorPrimary};
  margin-bottom: ${pxToRem(4)};
  margin-top: ${pxToRem(10)};
`;

const Subtitle = styled.p`
  font-size: ${pxToRem(14)};
  line-height: ${pxToRem(20)};
  color: ${themeColors.colorBlack};
  margin-bottom: ${pxToRem(16)};
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  column-gap: ${pxToRem(12)};

  & > * {
    flex: 1;
  }

  ${mediaBelow(variables.breakpoints.bpSm)} {
    flex-direction: column;
    row-gap: ${pxToRem(12)};
    column-gap: 0;

    & > * {
      width: 100%;
    }
  }
`;

const UpgradeBenifits = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  column-gap: 45px;
  margin-bottom: ${pxToRem(16)};
`;

const UpgradeBenifit = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  row-gap: 4px;
  max-width: 50px;
  text-align: center;
`;

const Benifit = styled.p`
  font-size: ${pxToRem(12)};
  line-height: ${pxToRem(14)};
  font-weight: 500;
  color: ${themeColors.colorPrimary};
`;

const CloseButton = styled.div`
  position: absolute;
  top: ${pxToRem(16)};
  right: ${pxToRem(16)};
  cursor: pointer;
`;

const UpgradeDeliveryOnlyAccount = ({ open, doItLaterClick, closeClick }) => {
  const { t } = useTranslation();
  const accountUid = useSelector(getCurrentAccountUid);
  const accountCode = useSelector(getCurrentAccountCode);
  const { RegistrationUrlV2 } = useSelector(selectSettings);
  const token = authScopeStringGetHelper(JWT_TOKEN);

  const registrationApi = RegistrationUrlV2;
  const goToRegistrationPage = () => {
    window.open(
      `${registrationApi}/choose-application?accountCode=${accountCode}&accountUid=${accountUid}&token=${token}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  return (
    <ModalOverlay isOpen={open}>
      <UpgradeModal isOpen={open}>
        <CloseButton onClick={() => closeClick(false)}>
          <Close width={12} height={12} />
        </CloseButton>

        <ContentWrapper>
          <VaultIcon width={74} height={74} />
          <Title>{t('buyWizard.accountUpgrade.title')}</Title>
          <Subtitle>{t('buyWizard.accountUpgrade.subtitle')}</Subtitle>
          <UpgradeBenifits>
            <UpgradeBenifit>
              <Insurace width={20} height={20} />
              <Benifit>{t('buyWizard.accountUpgrade.fullyInsured')}</Benifit>
            </UpgradeBenifit>
            <UpgradeBenifit>
              <FinancialProfit width={20} height={20} />
              <Benifit>
                {t('buyWizard.accountUpgrade.portfolioTracking')}
              </Benifit>
            </UpgradeBenifit>
          </UpgradeBenifits>
        </ContentWrapper>
        <Footer>
          <Button
            variant="primaryOutlined"
            textTransform="none"
            onClick={() => doItLaterClick()}
          >
            {t('buyWizard.accountUpgrade.doItLater')}
          </Button>
          <Button
            variant="primary"
            textTransform="none"
            onClick={() => {
              goToRegistrationPage();
            }}
          >
            {t('buyWizard.accountUpgrade.upgradeAccount')}
          </Button>
        </Footer>
      </UpgradeModal>
    </ModalOverlay>
  );
};

UpgradeDeliveryOnlyAccount.propTypes = {
  open: PropTypes.bool,
  doItLaterClick: PropTypes.func,
  closeClick: PropTypes.func,
};

export default UpgradeDeliveryOnlyAccount;
